import { Alert, Button, Chip, Group, ScrollArea, Stack, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useState } from 'react';
import { useConversationDebug } from 'src/secret-menu/conversation-debugger/hooks/useConversationDebug';
import ConversationFiles from 'src/secret-menu/conversation-debugger/ConversationFiles';
import ConversationHeader from 'src/secret-menu/conversation-debugger/ConversationHeader';
import MessageCard from 'src/secret-menu/conversation-debugger/MessageCard';
import PageLoader from 'src/UI/loader/PageLoader';

const ROLE_ORDER = ['system', 'user', 'assistant', 'tool'];

export default function ConversationViewer({
  conversationId,
  tenantName,
}: {
  conversationId: string;
  tenantName: string;
}) {
  const { conversation, isLoading, isError } = useConversationDebug(conversationId, tenantName);
  const [hiddenRoles, setHiddenRoles] = useState<string[]>([]);
  const [expandedIndices, setExpandedIndices] = useState<ReadonlySet<number>>(new Set());

  if (isLoading) {
    return <PageLoader absolute={false} />;
  }

  if (isError || !conversation) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />}>
        Failed to load this conversation. Check the conversation ID and tenant, then try again.
      </Alert>
    );
  }

  const messages = conversation.messages;
  const rolesPresent = [...new Set(messages.map(m => m.role))].sort(
    (a, b) =>
      (ROLE_ORDER.indexOf(a) + 1 || ROLE_ORDER.length + 1) -
      (ROLE_ORDER.indexOf(b) + 1 || ROLE_ORDER.length + 1),
  );
  const roleCounts = messages.reduce<Record<string, number>>((counts, m) => {
    counts[m.role] = (counts[m.role] ?? 0) + 1;
    return counts;
  }, {});
  const visibleRoles = rolesPresent.filter(role => !hiddenRoles.includes(role));
  const visibleMessages = messages
    .map((message, index) => ({ message, index }))
    .filter(({ message }) => visibleRoles.includes(message.role));

  const toggleMessage = (index: number) => {
    const next = new Set(expandedIndices);
    if (next.has(index)) {
      next.delete(index);
    } else {
      next.add(index);
    }
    setExpandedIndices(next);
  };

  return (
    <Stack gap="sm">
      <ConversationHeader conversation={conversation} />
      <ConversationFiles conversationId={conversationId} tenantName={tenantName} />

      <Group gap="xs" justify="space-between">
        <Chip.Group
          multiple
          value={visibleRoles}
          onChange={selected => setHiddenRoles(rolesPresent.filter(r => !selected.includes(r)))}
        >
          <Group gap={6}>
            {rolesPresent.map(role => (
              <Chip key={role} value={role} size="xs" variant="light">
                {role} ({roleCounts[role]})
              </Chip>
            ))}
          </Group>
        </Chip.Group>
        <Group gap={6}>
          <Button
            size="compact-xs"
            variant="subtle"
            color="gray"
            onClick={() => setExpandedIndices(new Set(visibleMessages.map(({ index }) => index)))}
          >
            Expand all
          </Button>
          <Button
            size="compact-xs"
            variant="subtle"
            color="gray"
            onClick={() => setExpandedIndices(new Set())}
          >
            Collapse all
          </Button>
        </Group>
      </Group>

      <ScrollArea h="60vh" type="auto">
        <Stack gap="xs" pr="sm">
          {visibleMessages.length === 0 && (
            <Text size="textSm" c="dimmed" ta="center" py="xl">
              No messages match the current filters.
            </Text>
          )}
          {visibleMessages.map(({ message, index }) => (
            <MessageCard
              key={index}
              message={message}
              index={index}
              expanded={expandedIndices.has(index)}
              onToggle={() => toggleMessage(index)}
            />
          ))}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}
